/* eslint-disable no-unused-vars */
import {
  IClientDetails,
  ISecurityIdentifierDetail,
  ITradeAllocation,
  TradeMessageSubType,
  TradeMessageType,
  TradeOpenClose,
} from "./ENUMBondTradeMessage";

// LQC-2674 - the POST /Trade/Swap message, read off
// LQ.Application/Trades/Swap/Incoming*.cs and the validation attributes in
// LQ.Domain/Attributes rather than inferred. Swaption (LQC-2677) reuses the leg shape.
//
// Every vocabulary below is a hardcoded C# list, NOT a DB table, so mirroring it here
// cannot drift silently. The one exception is the reference rate - see swapConventions.

export enum TradeInterestType {
  Fixed = "Fixed",
  Floating = "Floating",
}

// ValidateFrequencyAttribute. "Zero" is a real member - a zero-coupon leg.
// NOTE the FrequencyLeg enum also defines "12M", but the validator does NOT accept it,
// so it is deliberately absent here. Use "1Y".
export const SWAP_FREQUENCIES: string[] = [
  "1D",
  "1W",
  "2W",
  "3W",
  "4W",
  "1M",
  "2M",
  "3M",
  "4M",
  "5M",
  "6M",
  "1Y",
  "Zero",
];

// ValidateDayCountAttribute. Ordered by how often real swaps use them, not alphabetically.
export const SWAP_DAY_COUNTS: string[] = [
  "Act/365",
  "Act/360",
  "30/360",
  "Act/Act",
  "30/Act",
  "30/360 (SIA)",
  "30/365",
  "NL/ACT",
  "NL/360",
  "NL/365",
  "30E/Act",
  "30E/360",
  "30E/365",
  "30I/360",
  "30I/365",
  "30I/ACT",
  "30U/360",
  "30U/365",
  "30U/ACT",
  "ACT/365.FIXED",
  "DU/252",
  "ACT/360 (ICMA)",
  "ACT/365 (ICMA)",
  "30/360 (PSA)",
  "30/360 (ISDA)",
  "ACT/365 (Japanese)",
  "ACT/365 (ISDA)",
  "BUS/252",
  "Act/AFB",
  "1/1",
];

// ValidateBusinessDayAttribute. Real data holds both "Modified following" and
// "Modified Following" - ValidStringValuesAttribute compares case-insensitively, so
// neither spelling is a bug. Do not "fix" one to match the other.
export const SWAP_BUSINESS_DAYS: string[] = [
  "Modified following",
  "Following",
  "Actual",
  "Previous",
  "Modified previous",
];

// LCH only, on purpose. ValidateClearingHouseAttribute would accept 22 more (CME, ASX,
// SGX, HKEX, KRX, JSCC, SCH, ICCL, CCI, CC&G, ECC, EuroCCP, KDPW, SIX, MEFF, Iberclear,
// ICE, DTCC, OCC, NASDAQOMX, BM&F), but every one of them is COMMENTED OUT of the
// TradeClearingHouse enum, SwapModelService defaults a cleared swap to LCH by itself,
// and the spec (BUS-715 task B1) lists the enum as "TradeClearingHouse (LCH)". The loose
// string validator is the outlier, not the intent. Widen this only when the enum is.
export const SWAP_CLEARING_HOUSES: string[] = ["LCH"];

// A swap pays or receives - ValidateSwapDirection accepts nothing else.
export const SWAP_DIRECTIONS: string[] = ["Pay", "Receive"];

// LQC-2957 - the one TradeCPIIndex ValidateCPIIndex accepts per currency (Leg 2's).
export const CPI_INDEX_BY_CURRENCY: Record<string, string> = {
  AUD: "AUDCPI",
  USD: "USDCPI",
  EUR: "EURHICPxT",
  GBP: "UKRPI",
  JPY: "JPYJPCPI",
  SEK: "SEKCPI",
  NZD: "NZDCPI",
  CAD: "CADCPI",
  SGD: "SGDCPI",
};

// LQC-2991 - screen-only directions that mark a swap as ZCS. Never sent: mapped to
// Pay / Receive before the message is built.
export enum SwapInflationDirection {
  BuyInflation = "Buy Inflation",
  SellInflation = "Sell Inflation",
}

// One leg. couponRate and spread are both bounded to (-1, 1), so they travel as
// DECIMALS, not percents: 2.678521% is sent as 0.02678521.
export interface ISwapLegDetails {
  interestType: TradeInterestType;
  couponRate?: number;
  couponFrequency: string;
  resetFrequency?: string;
  referenceRate?: string;
  spread?: number;
  currency: string;
  currencyPayment?: string;
  dayCount: string;
  businessDay: string;
  calendar: string;
}

export interface ISwapSecurityDetails {
  startDate: string; // yyyy-MM-dd
  maturityDate: string; // yyyy-MM-dd
  firstCouponFrom?: string;
  firstCouponDate?: string;
  cleared: boolean;
  // ZCS only. The API bounds cpi to 0, null or > 1.
  cpi?: number;
  cpiIndex?: string;
  securityDetailsLeg1: ISwapLegDetails;
  securityDetailsLeg2: ISwapLegDetails;
}

export interface ISwapTradeDetails {
  tradeDate: string; // yyyy-MM-dd
  direction: string; // Pay | Receive
  nominal: number;
  level?: number;
  settlementDate: string; // yyyy-MM-dd
  openClose: TradeOpenClose;
  clearingHouse?: string;
  transactionNumber?: string;
}

export interface ISwapTradeMessage {
  messageType: TradeMessageType;
  subType: TradeMessageSubType;
  clientDetails: IClientDetails;
  // A swap needs no security lookup, so this stays empty the way a bond option's does.
  security: { identifierDetails?: ISecurityIdentifierDetail[] };
  securityDetails: ISwapSecurityDetails;
  tradeDetails: ISwapTradeDetails;
  allocations: ITradeAllocation[];
}
